import countriesData from "./countries.json";

/**
 * Canonical country catalog.
 *
 * The single source of truth is `countries.json`, which is also read by the
 * Rails backend (`CountryCatalog`) so the frontend and backend share one list
 * and cannot drift. This module only re-exports the typed shape the UI
 * consumes; do not add country entries here.
 */
export interface Country {
  name: string;
  alpha2: string;
}

export const COUNTRIES: Country[] = countriesData;

// Localizable languages (#288). English is intentionally absent: the canonical
// catalog names are already English, and keeping them untouched means the
// default locale renders exactly what the backend stores. Only the active
// non-English UI language is routed through the built-in Intl region data.
const REGION_DATA_LOCALES: Record<string, string> = {
  pt: 'pt',
  es: 'es',
};

const displayNamesByLocale = new Map<string, Intl.DisplayNames>();

const regionDisplayNames = (locale: string): Intl.DisplayNames | null => {
  const cached = displayNamesByLocale.get(locale);
  if (cached) {
    return cached;
  }

  try {
    const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
    displayNamesByLocale.set(locale, displayNames);
    return displayNames;
  } catch {
    // Older runtimes without Intl.DisplayNames fall back to the canonical
    // English name rather than dropping the option.
    return null;
  }
};

/**
 * Display name for a country in the active UI language.
 *
 * English (and any unsupported language) returns the canonical catalog name,
 * so the default locale is unchanged; PT/ES resolve from the built-in Intl
 * region data. The alpha2 code — never this label — is the stored/submitted
 * value, so switching language cannot change which country is recorded.
 */
export const getCountryDisplayName = (country: Country, language: string): string => {
  const locale = REGION_DATA_LOCALES[language.slice(0, 2).toLowerCase()];
  if (!locale) {
    return country.name;
  }

  const displayNames = regionDisplayNames(locale);
  return displayNames?.of(country.alpha2) ?? country.name;
};

/** `${localized name} - ${alpha2}` label shared by the country pickers. */
export const getCountryLabel = (country: Country, language: string): string =>
  `${getCountryDisplayName(country, language)} - ${country.alpha2}`;

/**
 * Match a filter query against a country's canonical name, its localized name
 * in the active language, and (for 1-2 character queries) its alpha2 code.
 * Mirrors the historic prefix rules so search keeps working as the displayed
 * names follow the UI language.
 */
export const countryMatchesSearch = (
  country: Country,
  search: string,
  language: string,
): boolean => {
  const query = search.toLowerCase().trim();
  if (!query) {
    return true;
  }

  const names = [
    country.name.toLowerCase(),
    getCountryDisplayName(country, language).toLowerCase(),
  ];

  if (query.length <= 2) {
    return (
      names.some((name) => name.startsWith(query)) ||
      country.alpha2.toLowerCase().startsWith(query)
    );
  }

  return names.some((name) => name.startsWith(query));
};
